import React, { useEffect, useRef } from "react"
import styles from "../../styles/scss/components/form/formSystem.module.scss"

// Form-level list of what's wrong, shown after a submit attempt fails.
// It takes focus on each failed submit (submitCount changes) so keyboard
// and screen reader users land on it; each entry links to its field.
//
// errors: [{ field, message }] — `field` is the id of the control to focus
// (for groups, the fieldset id; the first input inside it gets focus).
const ErrorSummary = ({ errors = [], submitCount = 0, title = "Please fix the following:" }) => {
    const ref = useRef(null)
    const focusedFor = useRef(0)
    const visible = submitCount > 0 && errors.length > 0

    // Formik bumps submitCount before validation resolves, so wait until the
    // errors are actually showing, then focus once per submit attempt (not on
    // every keystroke that edits the list).
    useEffect(() => {
        // resetForm() (e.g. moving between steps) sets submitCount back to 0;
        // forget the last attempt so the next failed submit takes focus again
        if (submitCount === 0) focusedFor.current = 0
        if (visible && ref.current && focusedFor.current !== submitCount) {
            focusedFor.current = submitCount
            ref.current.focus()
        }
    })

    if (!visible) return null

    const focusField = (e, field) => {
        const el = document.getElementById(field)
        if (!el) return
        e.preventDefault()
        const target = el.matches("input, select, textarea, button")
            ? el
            : el.querySelector("input, select, textarea, button")
        ;(target || el).focus()
        el.scrollIntoView({ block: "center" })
    }

    return (
        <div
            className={styles.summary}
            role="alert"
            tabIndex={-1}
            ref={ref}
            aria-labelledby="form-error-summary-title"
        >
            <p className={styles.summaryTitle} id="form-error-summary-title">
                {title}
            </p>
            <ul className={styles.summaryList}>
                {errors.map(e => (
                    <li key={e.field}>
                        <a href={`#${e.field}`} onClick={ev => focusField(ev, e.field)}>
                            {e.message}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    )
}

export default ErrorSummary
